'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  FolderOpenIcon,
  DocumentTextIcon,
  CalculatorIcon,
  Square3Stack3DIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  TableCellsIcon,
  TruckIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  BeakerIcon,
  CircleStackIcon,
  ChartBarSquareIcon,
} from '@heroicons/react/24/outline';

interface WorkflowStep {
  id: string;
  number: number;
  label: string;
  labelLt: string;
  href: string;
  icon: React.ElementType;
  description: string;
  requiredFor: string;
  checks: StepCheck[];
}

interface StepCheck {
  id: string;
  label: string;
  status: 'ok' | 'warning' | 'missing' | 'unknown';
  message?: string;
  action?: string;
}

interface AIMessage {
  role: 'assistant' | 'user';
  content: string;
  type?: 'info' | 'warning' | 'error' | 'success';
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    id: 'projektas',
    number: 1,
    label: 'Projektas',
    labelLt: 'Projektas',
    href: '/projects',
    icon: FolderOpenIcon,
    description: 'Sukurkite projektą su kliento pavadinimu, konkurso numeriu ir terminu.',
    requiredFor: 'Visi kiti žingsniai reikalauja aktyvaus projekto.',
    checks: [
      {
        id: 'project-exists',
        label: 'Aktyvus projektas',
        status: 'unknown',
        message: 'Patikrinkite ar turite sukurtą projektą',
        action: 'Sukurti projektą',
      },
      {
        id: 'project-deadline',
        label: 'Terminas nustatytas',
        status: 'unknown',
        message: 'Terminas reikalingas grafikui',
        action: 'Redaguoti projektą',
      },
    ],
  },
  {
    id: 'dokumentai',
    number: 2,
    label: 'Dokumentai',
    labelLt: 'Dokumentai',
    href: '/dokumentai',
    icon: DocumentTextIcon,
    description:
      'Įkelkite PDF brėžinius, sienų tipus ir specifikacijas. AI nuskaitys ir indeksuos.',
    requiredFor: 'Kiekių skaičiavimas ir žiniaraščiai.',
    checks: [
      {
        id: 'docs-uploaded',
        label: 'Dokumentai įkelti',
        status: 'unknown',
        message: 'Reikalingi bent brėžiniai arba specifikacija',
        action: 'Įkelti dokumentus',
      },
      {
        id: 'docs-analyzed',
        label: 'AI analizė atlikta',
        status: 'unknown',
        message: 'Paleiskite AI analizę po įkėlimo',
        action: 'Analizuoti',
      },
    ],
  },
  {
    id: 'fasadai',
    number: 3,
    label: 'Fasadai',
    labelLt: 'Fasadai & sienos',
    href: '/fasadai',
    icon: Square3Stack3DIcon,
    description: 'Apdorokite fasadų ir sienų išklotines, matavimus bei AI angų aptikimą.',
    requiredFor: 'Išsamus fasado dalių takeoff.',
    checks: [
      {
        id: 'drawings-exist',
        label: 'Brėžiniai takeoff moduliui',
        status: 'unknown',
        message: 'Brėžiniai reikalingi takeoff segmentams braižyti',
        action: 'Įkelti brėžinius',
      },
    ],
  },
  {
    id: 'sienu-tipai',
    number: 4,
    label: 'Sienų tipai',
    labelLt: 'Sienų tipai',
    href: '/wall-types',
    icon: Square3Stack3DIcon,
    description: 'Apibrėžkite projekto sienų tipus (WT2, CIW45 ir kt.) su aukščiais ir spalvomis.',
    requiredFor: 'AI kiekių skaičiavimas.',
    checks: [
      {
        id: 'wall-types-defined',
        label: 'Sienų tipai apibrėžti',
        status: 'unknown',
        message: 'Reikalingi sienų tipo kodai',
        action: 'Pridėti sienų tipus',
      },
    ],
  },
  {
    id: 'kiekiai',
    number: 5,
    label: 'Kiekių skaičiavimas',
    labelLt: 'AI Kiekių skaičiavimas',
    href: '/kiekiai',
    icon: CalculatorIcon,
    description: 'AI nuskaito brėžinius, aptinka sienų segmentus ir skaičiuoja ilgius.',
    requiredFor: 'Tikslūs kiekiai sąmatoje.',
    checks: [
      {
        id: 'takeoff-run',
        label: 'Takeoff paleistas',
        status: 'unknown',
        message: 'Paleiskite AI kiekių skaičiavimą',
        action: 'Paleisti takeoff',
      },
      {
        id: 'low-confidence-reviewed',
        label: 'Žemo pasitikėjimo peržiūrėti',
        status: 'unknown',
        message: 'Patikrinkite žymėtus segmentus',
        action: 'Peržiūrėti segmentus',
      },
    ],
  },
  {
    id: 'ziniarastiai',
    number: 6,
    label: 'Žiniaraščiai',
    labelLt: 'Žiniaraščiai (BOQ)',
    href: '/ziniarastiai',
    icon: ClipboardDocumentListIcon,
    description: 'Įkelkite arba įveskite darbų žiniaraštį. AI aptiks eilutes ir priskirys kiekius.',
    requiredFor: 'Sąmatos skaičiavimas.',
    checks: [
      {
        id: 'boq-uploaded',
        label: 'Žiniaraštis įkeltas',
        status: 'unknown',
        message: 'Reikalingas darbų žiniaraštis',
        action: 'Įkelti žiniaraštį',
      },
      {
        id: 'boq-reviewed',
        label: 'Eilutės peržiūrėtos',
        status: 'unknown',
        message: 'Patikrinkite žemo pasitikėjimo eilutes',
        action: 'Peržiūrėti',
      },
    ],
  },
  {
    id: 'formules',
    number: 7,
    label: 'Formulės',
    labelLt: 'Formulių DB',
    href: '/formules',
    icon: BeakerIcon,
    description: 'Apibrėžkite ir išbandykite matematines formules medžiagų išeigoms.',
    requiredFor: 'Automatizuotas medžiagų poreikio skaičiavimas.',
    checks: [
      {
        id: 'formulas-defined',
        label: 'Formulės apibrėžtos',
        status: 'unknown',
        message: 'Nustatykite išeigų skaičiavimo formules',
        action: 'Kurti formules',
      },
    ],
  },
  {
    id: 'ikainiai',
    number: 8,
    label: 'Įkainiai',
    labelLt: 'Įkainiai',
    href: '/ikainiai',
    icon: CurrencyEuroIcon,
    description: 'Palaikykite įmonės vidinę įkainių bazę — darbas, medžiagos, mechanizmai.',
    requiredFor: 'Sąmatos formulės.',
    checks: [
      {
        id: 'rates-current',
        label: 'Įkainiai atnaujinti',
        status: 'unknown',
        message: 'Patikrinkite ar įkainiai aktualūs',
        action: 'Peržiūrėti įkainius',
      },
      {
        id: 'overhead-set',
        label: 'Pridėtinės išlaidos nustatytos',
        status: 'unknown',
        message: 'Reikalingas pridėtinių išlaidų %',
        action: 'Nustatyti %',
      },
    ],
  },
  {
    id: 'norma',
    number: 9,
    label: 'NORMA normatyvai',
    labelLt: 'NORMA normatyvai',
    href: '/norma',
    icon: CircleStackIcon,
    description: 'Įkelkite arba koreguokite NORMA/Sistela normatyvus ir susiekite juos su MŽ.',
    requiredFor: 'Profesionalūs Sistela formato eksportai.',
    checks: [
      {
        id: 'norma-rates-exist',
        label: 'NORMA normatyvai įkelti',
        status: 'unknown',
        message: 'Importuokite arba sukurkite normatyvus',
        action: 'Valdyti NORMA',
      },
    ],
  },
  {
    id: 'objekto-salygos',
    number: 10,
    label: 'Objekto sąlygos',
    labelLt: 'Objekto sąlygos',
    href: '/mechanizmai',
    icon: TruckIcon,
    description: 'Įveskite aukštų skaičių, krono poreikį, privažiavimą ir sandėliavimą.',
    requiredFor: 'Mechanizmų ir logistikos kainos.',
    checks: [
      {
        id: 'site-conditions-filled',
        label: 'Sąlygos užpildytos',
        status: 'warning',
        message: 'Neužpildytos sąlygos tiesiogiai veikia mechanizmų kainas',
        action: 'Užpildyti sąlygas',
      },
    ],
  },
  {
    id: 'darbu-grafikas',
    number: 11,
    label: 'Grafikas',
    labelLt: 'Darbų grafikas',
    href: '/darbu-grafikas',
    icon: CalendarDaysIcon,
    description: 'Sudarykite darbų vykdymo kalendorinį grafiką bei etapus.',
    requiredFor: 'Projekto laiko ir resursų planavimas.',
    checks: [
      {
        id: 'schedule-stages-exist',
        label: 'Grafiko stadijos sukurtos',
        status: 'unknown',
        message: 'Apibrėžkite projekto vykdymo etapus',
        action: 'Sudaryti grafiką',
      },
    ],
  },
  {
    id: 'samata',
    number: 12,
    label: 'Sąmata',
    labelLt: 'Sąmata',
    href: '/samata',
    icon: TableCellsIcon,
    description: 'Platforma sujungia kiekius + sąlygas + įkainius ir apskaičiuoja galutinę kainą.',
    requiredFor: 'Galutinė kaina klientui.',
    checks: [
      {
        id: 'estimate-calculated',
        label: 'Sąmata apskaičiuota',
        status: 'unknown',
        message: 'Paleiskite sąmatos skaičiavimą',
        action: 'Skaičiuoti',
      },
      {
        id: 'estimate-reviewed',
        label: 'Sąmata peržiūrėta',
        status: 'unknown',
        message: 'Patvirtinkite prieš eksportą',
        action: 'Peržiūrėti',
      },
    ],
  },
  {
    id: 'tiekejai',
    number: 13,
    label: 'Tiekėjai',
    labelLt: 'Tiekėjų palyginimas',
    href: '/tiekeju-palyginimas',
    icon: ScaleIcon,
    description: 'Pridėkite tiekėjų pasiūlymus ir palyginkite juos šalia.',
    requiredFor: 'Optimalus tiekėjo pasirinkimas.',
    checks: [
      {
        id: 'suppliers-added',
        label: 'Tiekėjai pridėti',
        status: 'unknown',
        message: 'Rekomenduojama palyginti bent 2 tiekėjus',
        action: 'Pridėti tiekėją',
      },
    ],
  },
  {
    id: 'palyginimas',
    number: 14,
    label: 'Faktas',
    labelLt: 'Palyginimas su faktu',
    href: '/palyginimas',
    icon: ChartBarSquareIcon,
    description: 'Palyginkite sąmatos biudžetą su realiai patirtomis išlaidomis vykdymo metu.',
    requiredFor: 'Projekto pelningumo ir nukrypimų sekimas.',
    checks: [],
  },
  {
    id: 'rizikos',
    number: 15,
    label: 'Rizikos',
    labelLt: 'Rizikos',
    href: '/rizikos',
    icon: ShieldExclamationIcon,
    description: 'Peržiūrėkite AI aptiktas rizikas — trūkstamas kainas, žemą pasitikėjimą.',
    requiredFor: 'Saugus eksportas.',
    checks: [
      {
        id: 'risks-reviewed',
        label: 'Rizikos peržiūrėtos',
        status: 'unknown',
        message: 'Patikrinkite kritines rizikas prieš eksportą',
        action: 'Peržiūrėti rizikas',
      },
    ],
  },
  {
    id: 'eksportas',
    number: 16,
    label: 'Eksportas',
    labelLt: 'Eksportas',
    href: '/eksportas',
    icon: ArrowDownTrayIcon,
    description: 'Eksportuokite sąmatą, žiniaraštį, tiekėjų palyginimą kaip CSV/Excel.',
    requiredFor: 'Galutinis dokumentas klientui.',
    checks: [
      {
        id: 'export-ready',
        label: 'Paruošta eksportui',
        status: 'unknown',
        message: 'Įsitikinkite kad sąmata patvirtinta',
        action: 'Eksportuoti',
      },
    ],
  },
];

const STEP_HINTS: Record<string, string[]> = {
  projektas: [
    'Pradėkite nuo projekto sukūrimo — tai yra konteineris visiems kitiems duomenims.',
    'Nustatykite tikslų terminą — jis bus naudojamas darbų grafike.',
    'Kliento pavadinimas ir konkurso numeris padeda identifikuoti projektą eksporte.',
  ],
  dokumentai: [
    'Įkelkite visus PDF brėžinius vienu metu — AI apdoros juos lygiagrečiai.',
    'Kuo daugiau detalių brėžiniuose, tuo tikslesnė AI analizė.',
    'Specifikacijos PDF padeda AI suprasti medžiagų reikalavimus.',
  ],
  fasadai: [
    'Įkelkite fasadų išklotines arba brėžinius PDF formatu.',
    'Brėžiniuose pažymėkite matavimo mastelį prieš pradėdami matuoti.',
    'AI padės automatiškai aptikti langų ir durų angas fasaduose.',
  ],
  'sienu-tipai': [
    'Sienų tipo kodai turi atitikti brėžiniuose naudojamus kodus.',
    'Nustatykite tikslų aukštį — jis veikia kiekių skaičiavimą.',
    'Spalvos padeda vizualiai identifikuoti tipus brėžiniuose.',
  ],
  kiekiai: [
    'AI takeoff nuskaito brėžinius ir aptinka sienų segmentus automatiškai.',
    'Žemo pasitikėjimo segmentai reikalauja rankinio patvirtinimo.',
    'Neatpažinti segmentai turi būti priskirti rankiniu būdu.',
  ],
  ziniarastiai: [
    'Žiniaraštis gali būti įkeltas kaip Excel arba PDF — AI nuskaitys eilutes.',
    'Patikrinkite eilutes su pasitikėjimo balu žemiau 80% — jos pažymėtos oranžiškai.',
    'Galite rankiniu būdu koreguoti kiekius po AI analizės.',
  ],
  formules: [
    'Naudokite kintamuosius (pvz., Kiekis, Plotis, Aukštis) kurdami formules.',
    'Formulių šablonai leidžia automatiškai paskaičiuoti mišinio, armatūros ar klojinių išeigas.',
    'Išbandykite formulę Sandbox aplinkoje prieš pritaikydami ją visam projektui.',
  ],
  ikainiai: [
    'Įkainiai yra tiesos šaltinis visiems skaičiavimams — laikykite juos atnaujintus.',
    'Pridėtinių išlaidų % turėtų atspindėti realias įmonės išlaidas.',
    'Rizikos rezervas rekomenduojamas 5-15% priklausomai nuo projekto sudėtingumo.',
  ],
  norma: [
    'NORMA kodus galite priskirti automatiškai su AI arba rankiniu būdu.',
    'Importuokite savo kainyną iš XLS/XLSX failo, kad pritaikytumėte specifinius įkainius.',
    'Suderinti NORMA kodai automatiškai sugeneruos profesionalią Sistela formato sąmatą eksporte.',
  ],
  'objekto-salygos': [
    'Krono poreikis tiesiogiai veikia mechanizmų kainas — nepalikite tuščio.',
    'Privažiavimo kokybė veikia transporto išlaidas.',
    'Kiti rangovai objekte gali padidinti koordinavimo kainas.',
  ],
  'darbu-grafikas': [
    'Stadijas ir jų trukmes nustatykite pagal technologinius procesus (pvz., betonavimo kietėjimas).',
    'Susiekite grafiko etapus su BOQ pozicijomis, kad matytumėte resursų poreikį laike.',
    'Grafiką eksportuoti galite kartu su sąmata.',
  ],
  samata: [
    'Sąmata automatiškai perskaičiuojama keičiant įkainius ar kiekius.',
    'Kiekviena versija išsaugoma su pilnu formulės išskaidymu.',
    'Patikrinkite galutinę kainą su visais mokesčiais prieš eksportą.',
  ],
  tiekejai: [
    'Palyginkite bent 2-3 tiekėjus kiekvienai darbų kategorijai.',
    'Pažymėkite rekomenduojamą tiekėją — tai bude matoma eksporte.',
    'Užfiksuokite rizikas kiekvienam tiekėjui.',
  ],
  palyginimas: [
    'Palyginkite sąmatos biudžetą su realiai patirtomis išlaidomis statybos metu.',
    'Sekite nukrypimus nuo pradinio plano, kad išvengtumėte nuostolių.',
    'Faktinius kiekius ir kainas galite įvesti rankiniu būdu arba importuoti iš tiekėjų sąskaitų.',
  ],
  rizikos: [
    'Kritinės rizikas turi būti išspręstos prieš eksportą.',
    'AI automatiškai aptinka trūkstamas kainas ir žemą pasitikėjimą.',
    'Priskirkite atsakingą asmenį kiekvienai rizikai.',
  ],
  eksportas: [
    'CSV eksportas naudoja UTF-8 BOM — teisingai atidaro Excel lietuviškais simboliais.',
    'Eksportuokite visus dokumentus vienu metu prieš pateikiant klientui.',
    'JSON eksportas tinka archyvavimui ir integracijoms.',
  ],
};

interface GuidedWorkflowProps {
  isOpen: boolean;
  onClose: () => void;
}

const GuidedWorkflow: React.FC<GuidedWorkflowProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<AIMessage[]>([
    {
      role: 'assistant',
      content:
        'Sveiki! Aš esu jūsų AI asistentas. Galiu padėti suprasti kiekvieną darbo eigos žingsnį, paaiškinti kas trūksta, arba atsakyti į klausimus apie sąmatos procesą. Kuo galiu padėti?',
      type: 'info',
    },
  ]);
  const [activeStepHint, setActiveStepHint] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [dbChecks, setDbChecks] = useState<
    Record<string, { status: 'ok' | 'warning' | 'missing' | 'unknown'; message?: string }>
  >({});

  useEffect(() => {
    if (!isOpen) return;

    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/workflow/status');
        if (res.ok) {
          const data = await res.json();
          if (data.checks) {
            setDbChecks(data.checks);
          }
        }
      } catch (err) {
        console.error('Failed to fetch workflow status:', err);
      }
    };

    fetchStatus();
  }, [isOpen, pathname]);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5.4-mini', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Detect current step from pathname
  const currentStep = WORKFLOW_STEPS.find(
    (s) => pathname?.startsWith(s.href) || pathname === s.href
  );

  // Auto-expand current step
  useEffect(() => {
    if (currentStep) {
      setExpandedStep(currentStep.id);
    }
  }, [pathname]);

  // Stream AI response into chat
  const [streamingMsg, setStreamingMsg] = useState('');
  useEffect(() => {
    if (isLoading && response) {
      setStreamingMsg(response);
    }
    if (!isLoading && response && streamingMsg !== response) {
      setChatHistory((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '…') {
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        }
        return prev;
      });
      setStreamingMsg('');
    }
  }, [response, isLoading]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, streamingMsg]);

  const handleSendChat = () => {
    if (!chatInput.trim() || isLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatHistory((prev) => [
      ...prev,
      { role: 'user', content: userMsg },
      { role: 'assistant', content: '…' },
    ]);

    const getProjectStatusSummary = () => {
      if (!dbChecks || Object.keys(dbChecks).length === 0)
        return 'Projekto būsenos informacija kraunama...';
      const summaries = [];
      if (dbChecks['project-exists']?.status === 'ok') {
        summaries.push('Projektas sukurtas ir aktyvus.');
      } else {
        summaries.push('Nėra aktyvaus projekto.');
      }
      if (dbChecks['docs-uploaded']?.status === 'ok') {
        summaries.push(`Dokumentai: ${dbChecks['docs-uploaded'].message}.`);
      } else {
        summaries.push('Dokumentai neįkelti.');
      }
      if (dbChecks['drawings-exist']?.status === 'ok') {
        summaries.push(`Fasadai: ${dbChecks['drawings-exist'].message}.`);
      }
      if (dbChecks['boq-uploaded']?.status === 'ok') {
        summaries.push(`Žiniaraštis: ${dbChecks['boq-uploaded'].message}.`);
      } else {
        summaries.push('Žiniaraštis (BOQ) neįkeltas.');
      }
      if (dbChecks['formulas-defined']?.status === 'ok') {
        summaries.push(`Formulės: ${dbChecks['formulas-defined'].message}.`);
      }
      if (dbChecks['wall-types-defined']?.status === 'ok') {
        summaries.push(`Sienų tipai: ${dbChecks['wall-types-defined'].message}.`);
      } else {
        summaries.push('Sienų tipai neapibrėžti.');
      }
      if (dbChecks['takeoff-run']?.status === 'ok') {
        summaries.push('Takeoff matavimai atlikti.');
      } else {
        summaries.push('Takeoff matavimai neatlikti.');
      }
      if (dbChecks['norma-rates-exist']?.status === 'ok') {
        summaries.push(`NORMA normatyvai: ${dbChecks['norma-rates-exist'].message}.`);
      }
      if (dbChecks['schedule-stages-exist']?.status === 'ok') {
        summaries.push(`Darbų grafikas: ${dbChecks['schedule-stages-exist'].message}.`);
      }
      if (dbChecks['risks-reviewed']?.status === 'ok') {
        summaries.push('Rizikos visos išspręstos.');
      } else {
        summaries.push(`Rizikos: ${dbChecks['risks-reviewed']?.message || 'yra atvirų rizikų'}.`);
      }
      return summaries.join(' ');
    };

    const systemPrompt = `Tu esi Sąmata AI platformos asistentas statybos sąmatininkams Lietuvoje. 
Platforma turi šiuos darbo eigos žingsnius: Projektas → Dokumentai → Fasadai & sienos → Sienų tipai → Kiekių skaičiavimas → Žiniaraščiai → Formulių DB → Įkainiai → NORMA normatyvai → Objekto sąlygos → Darbų grafikas → Sąmata → Tiekėjų palyginimas → Palyginimas su faktu → Rizikos → Eksportas.
Dabartinis puslapis: ${pathname}.
Projekto statusas: ${getProjectStatusSummary()}

Atsakyk trumpai ir konkrečiai lietuvių kalba. Jei kažko trūksta arba yra problema, aiškiai pasakyk KAS trūksta, KODĖL tai svarbu ir KĄ reikia daryti. Naudok struktūruotus atsakymus su konkrečiais veiksmais.`;

    const messages = [
      { role: 'system', content: systemPrompt },
      ...chatHistory
        .filter((m) => m.content !== '…')
        .map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: userMsg },
    ];

    sendMessage(messages, { max_completion_tokens: 500 });
  };

  const getStepStatus = (step: WorkflowStep): 'complete' | 'current' | 'warning' | 'pending' => {
    if (pathname?.startsWith(step.href) || pathname === step.href) return 'current';
    const mergedChecks = step.checks.map((c) => {
      const dbCheck = dbChecks[c.id];
      return {
        ...c,
        status: dbCheck ? dbCheck.status : c.status,
      };
    });
    const hasWarning = mergedChecks.some((c) => c.status === 'warning' || c.status === 'missing');
    const allOk = mergedChecks.every((c) => c.status === 'ok');
    if (allOk && mergedChecks.length > 0) return 'complete';
    if (hasWarning) return 'warning';
    return 'pending';
  };

  const getStatusIcon = (status: 'complete' | 'current' | 'warning' | 'pending') => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon data-component-id="src/components/GuidedWorkflow.tsx:670:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="670" data-component-end-line="670" data-component-file="GuidedWorkflow.tsx" data-component-name="CheckCircleIcon" data-component-content="%7B%22elementName%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-emerald-500%22%7D" className="w-4 h-4 text-emerald-500" />;
      case 'current':
        return (
          <div data-component-id="src/components/GuidedWorkflow.tsx:673:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="673" data-component-end-line="675" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-4%20h-4%20rounded-full%20bg-blue-600%20flex%20items-center%20justify-center%22%7D" className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
            <div data-component-id="src/components/GuidedWorkflow.tsx:674:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="674" data-component-end-line="674" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-2%20h-2%20rounded-full%20bg-white%22%7D" className="w-2 h-2 rounded-full bg-white" />
          </div>
        );
      case 'warning':
        return <ExclamationTriangleIcon data-component-id="src/components/GuidedWorkflow.tsx:678:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="678" data-component-end-line="678" data-component-file="GuidedWorkflow.tsx" data-component-name="ExclamationTriangleIcon" data-component-content="%7B%22elementName%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-amber-500%22%7D" className="w-4 h-4 text-amber-500" />;
      default:
        return <div data-component-id="src/components/GuidedWorkflow.tsx:680:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="680" data-component-end-line="680" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-4%20h-4%20rounded-full%20border-2%20border-gray-300%22%7D" className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const getCheckIcon = (status: StepCheck['status']) => {
    switch (status) {
      case 'ok':
        return <CheckCircleIcon data-component-id="src/components/GuidedWorkflow.tsx:687:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="687" data-component-end-line="687" data-component-file="GuidedWorkflow.tsx" data-component-name="CheckCircleIcon" data-component-content="%7B%22elementName%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-emerald-500%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />;
      case 'warning':
        return <ExclamationTriangleIcon data-component-id="src/components/GuidedWorkflow.tsx:689:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="689" data-component-end-line="689" data-component-file="GuidedWorkflow.tsx" data-component-name="ExclamationTriangleIcon" data-component-content="%7B%22elementName%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-amber-500%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />;
      case 'missing':
        return <ExclamationTriangleIcon data-component-id="src/components/GuidedWorkflow.tsx:691:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="691" data-component-end-line="691" data-component-file="GuidedWorkflow.tsx" data-component-name="ExclamationTriangleIcon" data-component-content="%7B%22elementName%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-red-500%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />;
      default:
        return <InformationCircleIcon data-component-id="src/components/GuidedWorkflow.tsx:693:15" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="693" data-component-end-line="693" data-component-file="GuidedWorkflow.tsx" data-component-name="InformationCircleIcon" data-component-content="%7B%22elementName%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-gray-400%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div data-component-id="src/components/GuidedWorkflow.tsx:700:4" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="700" data-component-end-line="989" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20right-0%20top-0%20h-full%20w-80%20bg-%5B%230d1117%5D%20border-l%20border-gray-800%20flex%20flex-col%20z-50%20shadow-2xl%22%7D" className="fixed right-0 top-0 h-full w-80 bg-[#0d1117] border-l border-gray-800 flex flex-col z-50 shadow-2xl">
      {/* Header */}
      <div data-component-id="src/components/GuidedWorkflow.tsx:702:6" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="702" data-component-end-line="715" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20px-4%20py-3.5%20border-b%20border-gray-800%22%7D" className="flex items-center justify-between px-4 py-3.5 border-b border-gray-800">
        <div data-component-id="src/components/GuidedWorkflow.tsx:703:8" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="703" data-component-end-line="711" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2.5%22%7D" className="flex items-center gap-2.5">
          <div data-component-id="src/components/GuidedWorkflow.tsx:704:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="704" data-component-end-line="706" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-7%20h-7%20rounded-lg%20bg-blue-600%2F20%20border%20border-blue-500%2F30%20flex%20items-center%20justify-center%22%7D" className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
            <SparklesIcon data-component-id="src/components/GuidedWorkflow.tsx:705:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="705" data-component-end-line="705" data-component-file="GuidedWorkflow.tsx" data-component-name="SparklesIcon" data-component-content="%7B%22elementName%22%3A%22SparklesIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-blue-400%22%7D" className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div data-component-id="src/components/GuidedWorkflow.tsx:707:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="707" data-component-end-line="710" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/components/GuidedWorkflow.tsx:708:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="708" data-component-end-line="708" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-white%20leading-none%22%2C%22textContent%22%3A%22AI%20Vedlys%22%7D" className="text-sm font-semibold text-white leading-none">AI Vedlys</p>
            <p data-component-id="src/components/GuidedWorkflow.tsx:709:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="709" data-component-end-line="709" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-500%20mt-0.5%22%2C%22textContent%22%3A%22%C5%BDingsnis%20po%20%C5%BEingsnio%22%7D" className="text-xs text-gray-500 mt-0.5">Žingsnis po žingsnio</p>
          </div>
        </div>
        <button data-component-id="src/components/GuidedWorkflow.tsx:712:8" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="712" data-component-end-line="714" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-1.5%20rounded-lg%20hover%3Abg-gray-800%20transition-colors%22%7D" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
          <XMarkIcon data-component-id="src/components/GuidedWorkflow.tsx:713:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="713" data-component-end-line="713" data-component-file="GuidedWorkflow.tsx" data-component-name="XMarkIcon" data-component-content="%7B%22elementName%22%3A%22XMarkIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-gray-400%22%7D" className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Current step banner */}
      {currentStep && (
        <div data-component-id="src/components/GuidedWorkflow.tsx:719:8" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="719" data-component-end-line="746" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-3%20mt-3%20px-3%20py-2.5%20rounded-lg%20bg-blue-600%2F10%20border%20border-blue-500%2F20%22%7D" className="mx-3 mt-3 px-3 py-2.5 rounded-lg bg-blue-600/10 border border-blue-500/20">
          <div data-component-id="src/components/GuidedWorkflow.tsx:720:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="720" data-component-end-line="725" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-1%22%7D" className="flex items-center gap-2 mb-1">
            <div data-component-id="src/components/GuidedWorkflow.tsx:721:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="721" data-component-end-line="721" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-2%20h-2%20rounded-full%20bg-blue-400%20animate-pulse%22%7D" className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span data-component-id="src/components/GuidedWorkflow.tsx:722:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="722" data-component-end-line="724" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-blue-400%20uppercase%20tracking-wider%22%2C%22textContent%22%3A%22Dabartinis%20%C5%BEingsnis%22%7D" className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
              Dabartinis žingsnis
            </span>
          </div>
          <p data-component-id="src/components/GuidedWorkflow.tsx:726:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="726" data-component-end-line="728" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%20text-white%22%2C%22textContent%22%3A%22.%22%7D" className="text-sm font-medium text-white">
            {currentStep.number}. {currentStep.labelLt}
          </p>
          <p data-component-id="src/components/GuidedWorkflow.tsx:729:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="729" data-component-end-line="729" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-400%20mt-0.5%20leading-relaxed%22%7D" className="text-xs text-gray-400 mt-0.5 leading-relaxed">{currentStep.description}</p>
          {activeStepHint && (
            <div data-component-id="src/components/GuidedWorkflow.tsx:731:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="731" data-component-end-line="733" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20pt-2%20border-t%20border-blue-500%2F20%22%7D" className="mt-2 pt-2 border-t border-blue-500/20">
              <p data-component-id="src/components/GuidedWorkflow.tsx:732:14" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="732" data-component-end-line="732" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-blue-300%20leading-relaxed%22%7D" className="text-xs text-blue-300 leading-relaxed">{activeStepHint}</p>
            </div>
          )}
          <button data-component-id="src/components/GuidedWorkflow.tsx:735:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="735" data-component-end-line="745" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22mt-2%20flex%20items-center%20gap-1.5%20text-xs%20text-blue-400%20hover%3Atext-blue-300%20transition-colors%22%2C%22textContent%22%3A%22Patarimas%22%7D"
            onClick={() => {
              const hints = STEP_HINTS[currentStep.id] || [];
              const idx = hints.indexOf(activeStepHint || '');
              setActiveStepHint(hints[(idx + 1) % hints.length] || hints[0] || null);
            }}
            className="mt-2 flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            <LightBulbIcon data-component-id="src/components/GuidedWorkflow.tsx:743:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="743" data-component-end-line="743" data-component-file="GuidedWorkflow.tsx" data-component-name="LightBulbIcon" data-component-content="%7B%22elementName%22%3A%22LightBulbIcon%22%2C%22className%22%3A%22w-3%20h-3%22%7D" className="w-3 h-3" />
            Patarimas
          </button>
        </div>
      )}

      {/* Steps list */}
      <div data-component-id="src/components/GuidedWorkflow.tsx:750:6" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="750" data-component-end-line="874" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20px-3%20py-3%20space-y-1%22%7D" className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        <p data-component-id="src/components/GuidedWorkflow.tsx:751:8" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="751" data-component-end-line="753" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-gray-500%20uppercase%20tracking-wider%20px-1%20mb-2%22%2C%22textContent%22%3A%22Darbo%20eiga%22%7D" className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mb-2">
          Darbo eiga
        </p>
        {WORKFLOW_STEPS.map((step) => {
          const status = getStepStatus(step);
          const isExpanded = expandedStep === step.id;
          const StepIcon = step.icon;

          return (
            <div data-component-id="src/components/GuidedWorkflow.tsx:760:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="760" data-component-end-line="871" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={step.id}
              className={`rounded-lg border transition-all ${
                status === 'current'
                  ? 'border-blue-500/30 bg-blue-600/5'
                  : status === 'warning'
                    ? 'border-amber-500/20 bg-amber-500/5'
                    : 'border-gray-800 hover:border-gray-700'
              }`}
            >
              <button data-component-id="src/components/GuidedWorkflow.tsx:770:14" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="770" data-component-end-line="801" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20gap-2.5%20px-3%20py-2.5%20text-left%22%7D"
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
              >
                <div data-component-id="src/components/GuidedWorkflow.tsx:774:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="774" data-component-end-line="774" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%22%7D" className="flex-shrink-0">{getStatusIcon(status)}</div>
                <div data-component-id="src/components/GuidedWorkflow.tsx:775:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="775" data-component-end-line="783" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                    status === 'current' ? 'bg-blue-600/20' : 'bg-gray-800'
                  }`}
                >
                  <StepIcon data-component-id="src/components/GuidedWorkflow.tsx:780:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="780" data-component-end-line="782" data-component-file="GuidedWorkflow.tsx" data-component-name="StepIcon" data-component-content="%7B%22elementName%22%3A%22StepIcon%22%7D"
                    className={`w-3 h-3 ${status === 'current' ? 'text-blue-400' : 'text-gray-500'}`}
                  />
                </div>
                <div data-component-id="src/components/GuidedWorkflow.tsx:784:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="784" data-component-end-line="795" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                  <div data-component-id="src/components/GuidedWorkflow.tsx:785:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="785" data-component-end-line="794" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%22%7D" className="flex items-center gap-1.5">
                    <span data-component-id="src/components/GuidedWorkflow.tsx:786:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="786" data-component-end-line="786" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-gray-600%20font-mono%22%2C%22textContent%22%3A%22.%22%7D" className="text-xs text-gray-600 font-mono">{step.number}.</span>
                    <span data-component-id="src/components/GuidedWorkflow.tsx:787:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="787" data-component-end-line="793" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                      className={`text-sm font-medium truncate ${
                        status === 'current' ? 'text-white' : 'text-gray-300'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUpIcon data-component-id="src/components/GuidedWorkflow.tsx:797:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="797" data-component-end-line="797" data-component-file="GuidedWorkflow.tsx" data-component-name="ChevronUpIcon" data-component-content="%7B%22elementName%22%3A%22ChevronUpIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-gray-600%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon data-component-id="src/components/GuidedWorkflow.tsx:799:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="799" data-component-end-line="799" data-component-file="GuidedWorkflow.tsx" data-component-name="ChevronDownIcon" data-component-content="%7B%22elementName%22%3A%22ChevronDownIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-gray-600%20flex-shrink-0%22%7D" className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                )}
              </button>

              {isExpanded && (
                <div data-component-id="src/components/GuidedWorkflow.tsx:804:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="804" data-component-end-line="869" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20pb-3%20space-y-2%22%7D" className="px-3 pb-3 space-y-2">
                  <p data-component-id="src/components/GuidedWorkflow.tsx:805:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="805" data-component-end-line="805" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-500%20leading-relaxed%22%7D" className="text-xs text-gray-500 leading-relaxed">{step.description}</p>

                  {/* Checks */}
                  <div data-component-id="src/components/GuidedWorkflow.tsx:808:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="808" data-component-end-line="853" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-1.5%22%7D" className="space-y-1.5">
                    {step.checks.map((c) => {
                      const dbCheck = dbChecks[c.id];
                      const mergedCheck = {
                        ...c,
                        status: dbCheck ? dbCheck.status : c.status,
                        message: dbCheck && dbCheck.message ? dbCheck.message : c.message,
                      };
                      return (
                        <div data-component-id="src/components/GuidedWorkflow.tsx:817:24" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="817" data-component-end-line="850" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                          key={mergedCheck.id}
                          className={`flex items-start gap-2 px-2.5 py-2 rounded-md ${
                            mergedCheck.status === 'missing'
                              ? 'bg-red-500/10 border border-red-500/20'
                              : mergedCheck.status === 'warning'
                                ? 'bg-amber-500/10 border border-amber-500/20'
                                : mergedCheck.status === 'ok'
                                  ? 'bg-emerald-500/10 border border-emerald-500/20'
                                  : 'bg-gray-800/50 border border-gray-700/50'
                          }`}
                        >
                          {getCheckIcon(mergedCheck.status)}
                          <div data-component-id="src/components/GuidedWorkflow.tsx:830:26" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="830" data-component-end-line="849" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                            <p data-component-id="src/components/GuidedWorkflow.tsx:831:28" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="831" data-component-end-line="843" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
                              className={`text-xs font-medium ${
                                mergedCheck.status === 'missing'
                                  ? 'text-red-400'
                                  : mergedCheck.status === 'warning'
                                    ? 'text-amber-400'
                                    : mergedCheck.status === 'ok'
                                      ? 'text-emerald-400'
                                      : 'text-gray-400'
                              }`}
                            >
                              {mergedCheck.label}
                            </p>
                            {mergedCheck.message && (
                              <p data-component-id="src/components/GuidedWorkflow.tsx:845:30" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="845" data-component-end-line="847" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-500%20mt-0.5%20leading-relaxed%22%7D" className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                {mergedCheck.message}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Required for */}
                  <div data-component-id="src/components/GuidedWorkflow.tsx:856:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="856" data-component-end-line="859" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-1.5%20px-2%20py-1.5%20rounded-md%20bg-gray-800%2F30%22%7D" className="flex items-start gap-1.5 px-2 py-1.5 rounded-md bg-gray-800/30">
                    <InformationCircleIcon data-component-id="src/components/GuidedWorkflow.tsx:857:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="857" data-component-end-line="857" data-component-file="GuidedWorkflow.tsx" data-component-name="InformationCircleIcon" data-component-content="%7B%22elementName%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22w-3%20h-3%20text-gray-600%20flex-shrink-0%20mt-0.5%22%7D" className="w-3 h-3 text-gray-600 flex-shrink-0 mt-0.5" />
                    <p data-component-id="src/components/GuidedWorkflow.tsx:858:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="858" data-component-end-line="858" data-component-file="GuidedWorkflow.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-600%20leading-relaxed%22%7D" className="text-xs text-gray-600 leading-relaxed">{step.requiredFor}</p>
                  </div>

                  {/* Go to step */}
                  <Link data-component-id="src/components/GuidedWorkflow.tsx:862:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="862" data-component-end-line="868" data-component-file="GuidedWorkflow.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20w-full%20px-3%20py-2%20rounded-md%20bg-blue-600%2F10%20border%20border-blue-500%2F20%20hover%3Abg-blue-600%2F20%20transition-colors%20group%22%7D"
                    href={step.href}
                    className="flex items-center justify-between w-full px-3 py-2 rounded-md bg-blue-600/10 border border-blue-500/20 hover:bg-blue-600/20 transition-colors group"
                  >
                    <span data-component-id="src/components/GuidedWorkflow.tsx:866:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="866" data-component-end-line="866" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-medium%20text-blue-400%22%2C%22textContent%22%3A%22Eiti%20%C4%AF%20%C5%BEingsn%C4%AF%22%7D" className="text-xs font-medium text-blue-400">Eiti į žingsnį</span>
                    <ArrowRightIcon data-component-id="src/components/GuidedWorkflow.tsx:867:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="867" data-component-end-line="867" data-component-file="GuidedWorkflow.tsx" data-component-name="ArrowRightIcon" data-component-content="%7B%22elementName%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-blue-400%20group-hover%3Atranslate-x-0.5%20transition-transform%22%7D" className="w-3.5 h-3.5 text-blue-400 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* AI Chat */}
      <div data-component-id="src/components/GuidedWorkflow.tsx:877:6" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="877" data-component-end-line="988" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-gray-800%22%7D" className="border-t border-gray-800">
        <button data-component-id="src/components/GuidedWorkflow.tsx:878:8" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="878" data-component-end-line="892" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20px-4%20py-3%20hover%3Abg-gray-800%2F50%20transition-colors%22%7D"
          onClick={() => setChatOpen(!chatOpen)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/50 transition-colors"
        >
          <div data-component-id="src/components/GuidedWorkflow.tsx:882:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="882" data-component-end-line="886" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon data-component-id="src/components/GuidedWorkflow.tsx:883:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="883" data-component-end-line="883" data-component-file="GuidedWorkflow.tsx" data-component-name="ChatBubbleLeftRightIcon" data-component-content="%7B%22elementName%22%3A%22ChatBubbleLeftRightIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-blue-400%22%7D" className="w-4 h-4 text-blue-400" />
            <span data-component-id="src/components/GuidedWorkflow.tsx:884:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="884" data-component-end-line="884" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20text-gray-300%22%2C%22textContent%22%3A%22AI%20Asistentas%22%7D" className="text-sm font-medium text-gray-300">AI Asistentas</span>
            {isLoading && <div data-component-id="src/components/GuidedWorkflow.tsx:885:26" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="885" data-component-end-line="885" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1.5%20h-1.5%20rounded-full%20bg-blue-400%20animate-pulse%22%7D" className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />}
          </div>
          {chatOpen ? (
            <ChevronDownIcon data-component-id="src/components/GuidedWorkflow.tsx:888:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="888" data-component-end-line="888" data-component-file="GuidedWorkflow.tsx" data-component-name="ChevronDownIcon" data-component-content="%7B%22elementName%22%3A%22ChevronDownIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-gray-500%22%7D" className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRightIcon data-component-id="src/components/GuidedWorkflow.tsx:890:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="890" data-component-end-line="890" data-component-file="GuidedWorkflow.tsx" data-component-name="ChevronRightIcon" data-component-content="%7B%22elementName%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22w-4%20h-4%20text-gray-500%22%7D" className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {chatOpen && (
          <div data-component-id="src/components/GuidedWorkflow.tsx:895:10" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="895" data-component-end-line="986" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col" style={{ height: '260px' }}>
            {/* Messages */}
            <div data-component-id="src/components/GuidedWorkflow.tsx:897:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="897" data-component-end-line="943" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20px-3%20py-2%20space-y-2%22%7D" className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
              {chatHistory.map((msg, i) => (
                <div data-component-id="src/components/GuidedWorkflow.tsx:899:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="899" data-component-end-line="912" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div data-component-id="src/components/GuidedWorkflow.tsx:903:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="903" data-component-end-line="911" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                    className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-gray-800 text-gray-300 rounded-bl-sm border border-gray-700'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && streamingMsg && (
                <div data-component-id="src/components/GuidedWorkflow.tsx:915:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="915" data-component-end-line="920" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-start%22%7D" className="flex justify-start">
                  <div data-component-id="src/components/GuidedWorkflow.tsx:916:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="916" data-component-end-line="919" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-%5B85%25%5D%20px-3%20py-2%20rounded-xl%20rounded-bl-sm%20text-xs%20leading-relaxed%20bg-gray-800%20text-gray-300%20border%20border-gray-700%22%7D" className="max-w-[85%] px-3 py-2 rounded-xl rounded-bl-sm text-xs leading-relaxed bg-gray-800 text-gray-300 border border-gray-700">
                    {streamingMsg}
                    <span data-component-id="src/components/GuidedWorkflow.tsx:918:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="918" data-component-end-line="918" data-component-file="GuidedWorkflow.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-block%20w-1%20h-3%20bg-blue-400%20ml-0.5%20animate-pulse%22%7D" className="inline-block w-1 h-3 bg-blue-400 ml-0.5 animate-pulse" />
                  </div>
                </div>
              )}
              {isLoading && !streamingMsg && (
                <div data-component-id="src/components/GuidedWorkflow.tsx:923:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="923" data-component-end-line="940" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-start%22%7D" className="flex justify-start">
                  <div data-component-id="src/components/GuidedWorkflow.tsx:924:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="924" data-component-end-line="939" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20py-2%20rounded-xl%20rounded-bl-sm%20bg-gray-800%20border%20border-gray-700%22%7D" className="px-3 py-2 rounded-xl rounded-bl-sm bg-gray-800 border border-gray-700">
                    <div data-component-id="src/components/GuidedWorkflow.tsx:925:20" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="925" data-component-end-line="938" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-1%22%7D" className="flex gap-1">
                      <div data-component-id="src/components/GuidedWorkflow.tsx:926:22" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="926" data-component-end-line="929" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1.5%20h-1.5%20rounded-full%20bg-gray-500%20animate-bounce%22%7D"
                        className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <div data-component-id="src/components/GuidedWorkflow.tsx:930:22" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="930" data-component-end-line="933" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1.5%20h-1.5%20rounded-full%20bg-gray-500%20animate-bounce%22%7D"
                        className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <div data-component-id="src/components/GuidedWorkflow.tsx:934:22" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="934" data-component-end-line="937" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1.5%20h-1.5%20rounded-full%20bg-gray-500%20animate-bounce%22%7D"
                        className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div data-component-id="src/components/GuidedWorkflow.tsx:942:14" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="942" data-component-end-line="942" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={chatEndRef} />
            </div>

            {/* Quick prompts */}
            <div data-component-id="src/components/GuidedWorkflow.tsx:946:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="946" data-component-end-line="958" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20pb-1%20flex%20gap-1.5%20overflow-x-auto%22%7D" className="px-3 pb-1 flex gap-1.5 overflow-x-auto">
              {['Kas trūksta?', 'Kitas žingsnis?', 'Paaiškink formulę'].map((q) => (
                <button data-component-id="src/components/GuidedWorkflow.tsx:948:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="948" data-component-end-line="956" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-shrink-0%20text-xs%20px-2.5%20py-1%20rounded-full%20bg-gray-800%20text-gray-400%20hover%3Abg-gray-700%20hover%3Atext-gray-300%20border%20border-gray-700%20transition-colors%22%7D"
                  key={q}
                  onClick={() => {
                    setChatInput(q);
                  }}
                  className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 border border-gray-700 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>

            {/* Input */}
            <div data-component-id="src/components/GuidedWorkflow.tsx:961:12" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="961" data-component-end-line="985" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20pb-3%20pt-1%22%7D" className="px-3 pb-3 pt-1">
              <div data-component-id="src/components/GuidedWorkflow.tsx:962:14" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="962" data-component-end-line="984" data-component-file="GuidedWorkflow.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20bg-gray-800%20border%20border-gray-700%20rounded-xl%20px-3%20py-2%22%7D" className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2">
                <input data-component-id="src/components/GuidedWorkflow.tsx:963:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="963" data-component-end-line="976" data-component-file="GuidedWorkflow.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AchatInput%5D%22%2C%22className%22%3A%22flex-1%20bg-transparent%20text-xs%20text-gray-300%20placeholder-gray-600%20outline-none%22%7D"
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChat();
                    }
                  }}
                  placeholder="Klauskite AI..."
                  className="flex-1 bg-transparent text-xs text-gray-300 placeholder-gray-600 outline-none"
                  disabled={isLoading}
                />
                <button data-component-id="src/components/GuidedWorkflow.tsx:977:16" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="977" data-component-end-line="983" data-component-file="GuidedWorkflow.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-6%20h-6%20rounded-lg%20bg-blue-600%20hover%3Abg-blue-500%20disabled%3Aopacity-40%20disabled%3Acursor-not-allowed%20flex%20items-center%20justify-center%20transition-colors%20flex-shrink-0%22%7D"
                  onClick={handleSendChat}
                  disabled={!chatInput.trim() || isLoading}
                  className="w-6 h-6 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <PaperAirplaneIcon data-component-id="src/components/GuidedWorkflow.tsx:982:18" data-component-path="src/components/GuidedWorkflow.tsx" data-component-line="982" data-component-end-line="982" data-component-file="GuidedWorkflow.tsx" data-component-name="PaperAirplaneIcon" data-component-content="%7B%22elementName%22%3A%22PaperAirplaneIcon%22%2C%22className%22%3A%22w-3%20h-3%20text-white%22%7D" className="w-3 h-3 text-white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidedWorkflow;
